import * as moment from 'moment';
import { Address, InsuranceTypeReportGroups } from '.';
import { MEDICARE_INSURANCE_TYPE, MEDICARE_REPL_INSURANCE_TYPE } from '../constants';

export enum InsuranceType {
  Primary = 1,
  Secondary = 2,
  Tertiary = 3,
}

export enum InsuranceTypeDesc {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Tertiary = 'Tertiary',
}

export enum Gender {
  Male = 'M',
  Female = 'F',
}

export enum InsuranceStatus {
  Active = 'Active',
  Pending = 'Pending',
  NotActive = 'Not Active',
}

// API Models
export interface ApiOrderDetailsInsuranceItemResponse {
  insuranceId: number;
  insuranceName?: string;
  nameOfInsured?: string;
  relationshipToInsured?: string;
  insuranceNo?: string;
  insuranceGroupNo?: string;
  reportGroup?: string;
}

export interface ApiInsuranceCardItemResponse {
  patientInsuranceId: number;
  insuranceCompanyName?: string;
  nameOfInsured?: string;
  insuranceNumber?: string;
  groupNumber?: string;
  activeDesc?: InsuranceStatus;
  insuranceTypeDesc?: InsuranceTypeDesc;
}

export interface ApiPatientInsuranceItemResponse extends ApiOrderDetailsInsuranceItemResponse {
  insuranceType: InsuranceType;
}

export interface ApiPatientInsuranceResponse {
  patientInsuranceData: ApiPatientInsuranceItemResponse[];
}

export interface ApiRelationshipToInsuredResponse {
  lookupCode: string;
  lookupDescription: string;
}

export interface ApiPatientInsurancePolicy {
  insuranceType: InsuranceType;
  insuranceIdNumber: string;
  insuranceName: string;
  insuranceGroupNumber?: string;
  insurancePhone: string; // ex: '1234567890'
  // Policy holder information
  patientRelationship: number; // 1 - self, 2 - spouse, 3 - dependent
  phoneNumber: string;
  birthDate: string;
  firstName: string;
  lastName: string;
  middleInitial?: string;
  gender: Gender;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  employer?: string;
  suffix?: string;
}

// Front End Models

export interface Insurance {
  id: number;
  insurancePlan?: string;
  nameOfInsured?: string;
  relationshipToInsured?: string;
  insuranceNumber?: string | null;
  groupNumber?: string | null;
  status?: InsuranceStatus;
  type?: InsuranceType;
  typeDesc?: InsuranceTypeDesc;
}

export interface PatientInsurance {
  primaryInsurance?: Insurance;
  secondaryInsurance?: Insurance;
  // Computed
  isMedicareOrMedicareRepl: boolean;
}

export interface RelationshipToInsured {
  name: string;
  value: number;
}

// Form Models

export interface PolicyHolderFormValues extends Address {
  firstName: string;
  lastName: string;
  dob: string;
  gender: Gender;
  phoneNumber: string;
  line1: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface InsuranceFormValues {
  insurancePlan: string;
  insuranceNumber: string;
  groupNumber?: string | null;
  insurancePhone: string;
  relationshipToInsured: number;
  policyHolderInformation: PolicyHolderFormValues;
  insuranceType?: InsuranceType;
}

// Mappers
export const mapInsuranceItem = (i: ApiOrderDetailsInsuranceItemResponse): Insurance => {
  return {
    id: i.insuranceId,
    insurancePlan: i.insuranceName,
    nameOfInsured: i.nameOfInsured,
    relationshipToInsured: i.relationshipToInsured,
    insuranceNumber: i.insuranceNo,
    groupNumber: i.insuranceGroupNo,
  };
};

export const mapPatientInsurance = (
  pi: ApiPatientInsuranceResponse,
  insuranceGroups: InsuranceTypeReportGroups[]
): PatientInsurance => {
  const apiPrimaryInsurance = pi.patientInsuranceData.find((i) => i.insuranceType === InsuranceType.Primary);
  const apiSecondaryInsurance = pi.patientInsuranceData.find((i) => i.insuranceType === InsuranceType.Secondary);
  const medicareInsuranceGroups = insuranceGroups.filter(
    (ig) => ig.insuranceTypeName === MEDICARE_INSURANCE_TYPE || ig.insuranceTypeName === MEDICARE_REPL_INSURANCE_TYPE
  );
  let isMedicareOrMedicareRepl = false;

  if (apiPrimaryInsurance?.reportGroup) {
    medicareInsuranceGroups.forEach((ig) => {
      if (ig.insuranceReportGroups.find((g) => g === apiPrimaryInsurance?.reportGroup)) {
        isMedicareOrMedicareRepl = true;
      }
    });
  }

  return {
    primaryInsurance: apiPrimaryInsurance ? mapInsuranceItem(apiPrimaryInsurance) : undefined,
    secondaryInsurance: apiSecondaryInsurance ? mapInsuranceItem(apiSecondaryInsurance) : undefined,
    isMedicareOrMedicareRepl,
  };
};

export const mapInsuranceFormValues = (f: InsuranceFormValues, relationships: RelationshipToInsured[]): Insurance => {
  return {
    ...f,
    id: -1,
    relationshipToInsured: relationships.find((r) => r.value === f.relationshipToInsured)?.name || undefined,
  };
};

export const mapRelationshipToInsured = (r: ApiRelationshipToInsuredResponse): RelationshipToInsured => {
  return {
    name: r.lookupDescription,
    value: parseInt(r.lookupCode),
  };
};

export const mapInsuranceType = (desc?: InsuranceTypeDesc): InsuranceType | undefined => {
  switch (desc) {
    case InsuranceTypeDesc.Primary:
      return InsuranceType.Primary;
    case InsuranceTypeDesc.Secondary:
      return InsuranceType.Secondary;
    case InsuranceTypeDesc.Tertiary:
      return InsuranceType.Tertiary;
    default:
      return;
  }
};

export const mapInsuranceCard = (apiCard: ApiInsuranceCardItemResponse): Insurance => {
  return {
    id: apiCard.patientInsuranceId,
    insurancePlan: apiCard.insuranceCompanyName,
    nameOfInsured: apiCard.nameOfInsured,
    insuranceNumber: apiCard.insuranceNumber,
    groupNumber: apiCard.groupNumber,
    status: apiCard.activeDesc,
    type: mapInsuranceType(apiCard.insuranceTypeDesc),
    typeDesc: apiCard.insuranceTypeDesc,
  };
};

export const buildNewInsurancePolicyRequestData = (
  insuranceValues: InsuranceFormValues,
  insuranceType?: InsuranceType
): ApiPatientInsurancePolicy => {
  const submittedInsuranceType = insuranceValues.insuranceType || insuranceType || InsuranceType.Primary;

  return {
    insuranceType: submittedInsuranceType,
    insuranceIdNumber: insuranceValues.insuranceNumber,
    insuranceName: insuranceValues.insurancePlan,
    insuranceGroupNumber: !!insuranceValues.groupNumber ? insuranceValues.groupNumber : undefined,
    insurancePhone: insuranceValues.insurancePhone,
    patientRelationship: insuranceValues.relationshipToInsured,
    phoneNumber: insuranceValues.policyHolderInformation.phoneNumber,
    birthDate: moment(insuranceValues.policyHolderInformation.dob).format('YYYY-MM-DD'),
    firstName: insuranceValues.policyHolderInformation.firstName,
    lastName: insuranceValues.policyHolderInformation.lastName,
    gender: insuranceValues.policyHolderInformation.gender,
    street: insuranceValues.policyHolderInformation.line1,
    city: insuranceValues.policyHolderInformation.city,
    state: insuranceValues.policyHolderInformation.state,
    postalCode: insuranceValues.policyHolderInformation.zipCode.replace(/-/g, ''),
  };
};
